import {
    Form,
    FormPayload,
    LandingPage,
    LandingPagePayload,
    ListPayload,
    PreviewPageType,
    Template,
    TemplateType,
} from '@dev/ee-api/ee-api-models';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';
import { EditorMultiSignUpEditMessage, EditorSaveData, EditorSaveError } from '@dev/template-editor';
import { ApplicationState } from '..';
import {
    AddContactListErrorStatusAction,
    AddContactListStartAction,
    CancelEditStepAction,
    CloseAction,
    CloseGalleryAction,
    CloseUpsellingOverlayAction,
    EditorFile,
    FilesType,
    LandingPageCreatorActionTypes,
    LandingPageCreatorState,
    LandingPageErrors,
    LandingPageMetaData,
    LoadErrorAction,
    LoadPageTemplateErrorAction,
    LoadPageTemplateStartAction,
    LoadPageTemplateSuccessAction,
    LoadStartAction,
    LoadSuccessAction,
    ModifyLandingPageAction,
    ModifyLandingPageMetaDataAction,
    OpenGalleryAction,
    OpenUpsellingOverlayAction,
    ResetAction,
    SaveAllFormsErrorAction,
    SaveAllFormsSuccessAction,
    SaveErrorAction,
    SaveStartAction,
    SaveSuccessAction,
    SetDefaultTemplateAction,
    SetIsTemplateOpenAction,
    SetLandingPageErrorsAction,
    SetTemplatePreviewAction,
    StartEditStepAction,
    TemplateChangedAction,
    TemplateDeselectAction,
    TemplateFormAddLoadedAction,
    TemplateFormAddNewAction,
    TemplateFormDuplicatedAction,
    TemplateFormErrorNames,
    TemplateFormsFromReplacedTemplateClearAction,
    TemplateFormPayloadUpdateAction,
    TemplateFormsCheckInvalidAction,
    TemplateFormSetErrorsAction,
    TemplateFormSetMailingPreferencesLabelAction,
    TemplateFormsRemoveAction,
    TemplateFormsSetDefaultDoubleOptInDisabledAction,
    TemplateFormsSetInvalidAction,
    TemplateFormUpdateSavedAction,
    TemplateIncrementAutosaveCounterAction,
    TemplateLoadErrorAction,
    TemplateLoadSingleErrorAction,
    TemplateLoadSingleStartAction,
    TemplateLoadSingleSuccessAction,
    TemplateLoadStartAction,
    TemplateLoadSuccessAction,
    TemplateOpenFromGalleryAction,
    TemplatePreviewFromGalleryAction,
    TemplateShowSaveErrorsAction,
    TemplateUploadFileAction,
    TemplateUploadFileErrorAction,
    TemplateUploadFileStartAction,
    TemplateUploadFileSuccessAction,
    TemplateSetMultiSignUpEditMessageAction,
    TemplateSaveSettings,
    UpdateTemplateSaveSettingsAction
} from './types';

export const LoadStart = (name: string): LoadStartAction => ({
    type: LandingPageCreatorActionTypes.LOAD_START,
    payload: { name },
});

export const LoadSuccess = (
    landingPageObject: LandingPage,
    templateObject: Template,
    landingMetaJson: string
): LoadSuccessAction => ({
    type: LandingPageCreatorActionTypes.LOAD_SUCCESS,
    payload: { landingPageObject, templateObject, landingMetaJson },
});

export const LoadError = (): LoadErrorAction => ({
    type: LandingPageCreatorActionTypes.LOAD_ERROR,
});

export const LoadPageTemplateStart = (pageTemplateType: PreviewPageType): LoadPageTemplateStartAction => ({
    type: LandingPageCreatorActionTypes.LOAD_PAGE_TEMPLATE_START,
    payload: { pageTemplateType },
});

export const LoadPageTemplateSuccess = (
    pageTemplateType: PreviewPageType,
    pageTemplate: string
): LoadPageTemplateSuccessAction => ({
    type: LandingPageCreatorActionTypes.LOAD_PAGE_TEMPLATE_SUCCESS,
    payload: { pageTemplateType, pageTemplate },
});

export const LoadPagePreviewError = (): LoadPageTemplateErrorAction => ({
    type: LandingPageCreatorActionTypes.LOAD_PAGE_TEMPLATE_ERROR,
});

export const SaveStart = ({
    data,
    exitOnSave,
    errors,
    isSaveBlocked,
}: {
    data?: EditorSaveData;
    exitOnSave?: boolean;
    errors?: EditorSaveError[];
    isSaveBlocked?: boolean;
}): SaveStartAction => ({
    type: LandingPageCreatorActionTypes.SAVE_START,
    payload: { data, exitOnSave, errors, isSaveBlocked },
    meta: {
        thunk: true,
    },
});


export const SaveSuccess = (
    landingPageObject: LandingPage,
    templateObject: Template,
    editorSaveData?: EditorSaveData,
    landingPageMetaDataObject?: LandingPageMetaData,
    meta?: Record<string, unknown>
): SaveSuccessAction => ({
    type: LandingPageCreatorActionTypes.SAVE_SUCCESS,
    payload: { landingPageObject, templateObject, editorSaveData, landingPageMetaDataObject },
    meta,
});
export const SaveAllFormsSuccess = (meta?: Record<string, unknown>): SaveAllFormsSuccessAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_FORM_SAVE_ALL_SUCCESS,
    meta,
});

export const SaveError = (meta?: Record<string, unknown>): SaveErrorAction => ({
    type: LandingPageCreatorActionTypes.SAVE_ERROR,
    meta,
    error: true,
});
export const SaveAllFormsError = (meta?: Record<string, unknown>): SaveAllFormsErrorAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_FORM_SAVE_ALL_ERROR,
    meta,
    error: true,
});

export const ModifyLandingPage = (
    landingPagePayload: LandingPagePayload & { LandingUrl?: string }
): ModifyLandingPageAction => ({
    type: LandingPageCreatorActionTypes.MODIFY_LANDING_PAGE,
    payload: {
        landingPagePayload,
    },
});

export const ModifyLandingPageMetaData = (
    landingPageMetaDataPayload: LandingPageMetaData
): ModifyLandingPageMetaDataAction => ({
    type: LandingPageCreatorActionTypes.MODIFY_LANDING_PAGE_METADATA,
    payload: {
        landingPageMetaDataPayload,
    },
});

export const StartEditStep = (stepInEdit: number): StartEditStepAction => ({
    type: LandingPageCreatorActionTypes.START_EDIT_STEP,
    payload: { stepInEdit },
});

export const CancelEditStep = (): CancelEditStepAction => ({
    type: LandingPageCreatorActionTypes.CANCEL_EDIT_STEP,
});

export const Close = (): CloseAction => ({
    type: LandingPageCreatorActionTypes.CLOSE,
});

export const TemplateUploadFile = (file: File, blockId: string): TemplateUploadFileAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_UPLOAD_FILE,
    payload: {
        file,
        blockId,
    },
});

export const TemplateUploadFileStart = (): TemplateUploadFileStartAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_UPLOAD_FILE_START,
});

export const TemplateUploadFileSuccess = (filesList: EditorFile[]): TemplateUploadFileSuccessAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_UPLOAD_FILE_SUCCESS,
    payload: {
        filesList,
    },
});

export const TemplateUploadFileError = (): TemplateUploadFileErrorAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_UPLOAD_FILE_ERROR,
});

export const SetDefaultTemplate = ({ templateObject }: { templateObject: Template }): SetDefaultTemplateAction => ({
    type: LandingPageCreatorActionTypes.SET_DEFAULT_TEMPLATE,
    payload: { templateObject },
});

export const TemplateLoadStart = (name?: string, isPublic = false, useDraftScope = false): TemplateLoadStartAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_LOAD_START,
    payload: { name, isPublic, useDraftScope },
});

export const TemplateLoadSuccess = (
    templateObject: Template,
    isPublic: boolean,
    useDraftScope: boolean
): TemplateLoadSuccessAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_LOAD_SUCCESS,
    payload: { templateObject, isPublic, useDraftScope },
});

export const TemplateLoadError = (): TemplateLoadErrorAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_LOAD_ERROR,
});

export const TemplateIncrementAutosaveCounter = (): TemplateIncrementAutosaveCounterAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_INCREMENT_AUTOSAVE_COUNTER,
});

export const TemplateFormAddNew = (blockClientId: string): TemplateFormAddNewAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_FORM_ADD_NEW,
    payload: {
        blockClientId,
    },
});

export const TemplateFormsRemove = (formsBlockClientIds: string[]): TemplateFormsRemoveAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_FORMS_REMOVE,
    payload: {
        formsBlockClientIds,
    },
});

export const TemplateFormsFromReplacedTemplateClear = (): TemplateFormsFromReplacedTemplateClearAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_FORMS_FROM_REPLACED_TEMPLATE_CLEAR,
});

export const TemplateFormUpdateSaved = (blockClientId: string, form: Form): TemplateFormUpdateSavedAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_FORM_UPDATE_SAVED,
    payload: {
        blockClientId,
        form,
    },
});

export const TemplateFormAddLoaded = (
    blockClientId: string,
    form: Form,
    formMailingPreferencesLabel?: string
): TemplateFormAddLoadedAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_FORM_ADD_LOADED,
    payload: {
        blockClientId,
        form,
        formMailingPreferencesLabel,
    },
});

export const TemplateFormSetMailingPreferencesLabel = (
    blockClientId: string,
    formMailingPreferencesLabel?: string
): TemplateFormSetMailingPreferencesLabelAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_FORM_SET_MAILING_PREFERENCES_LABEL,
    payload: {
        blockClientId,
        formMailingPreferencesLabel,
    },
});

export const TemplateFormDuplicated = (
    blockClientId: string,
    newBlockClientId: string
): TemplateFormDuplicatedAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_FORM_DUPLICATED,
    payload: {
        blockClientId,
        newBlockClientId,
    },
});

export const TemplateFormPayloadUpdate = (
    blockClientId: string,
    formPayload: Partial<FormPayload>
): TemplateFormPayloadUpdateAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_FORM_PAYLOAD_UPDATE,
    payload: {
        blockClientId,
        formPayload,
    },
});

export const TemplateFormsCheckInvalid = (blockClientIds?: string[]): TemplateFormsCheckInvalidAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_FORMS_CHECK_INVALID,
    payload: {
        blockClientIds,
    },
});

export const TemplateFormsSetInvalid = (blockClientIds: string[]): TemplateFormsSetInvalidAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_FORMS_SET_INVALID,
    payload: {
        blockClientIds,
    },
});

export const TemplateFormsSetDefaultDoubleOptInDisabled = (
    isDoubleOptInDisabledByDefault: boolean
): TemplateFormsSetDefaultDoubleOptInDisabledAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_FORMS_SET_DEFAULT_DOUBLE_OPT_IN,
    payload: {
        isDoubleOptInDisabledByDefault,
    },
});

export const TemplateFormSetErrors = (
    errors: Partial<Record<TemplateFormErrorNames, boolean>>,
    blockClientId: string
): TemplateFormSetErrorsAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_FORM_SET_ERRORS,
    payload: {
        errors,
        blockClientId,
    },
});

export const TemplateSetMultiSignUpEditMessage = (
    editMessage?: EditorMultiSignUpEditMessage
): TemplateSetMultiSignUpEditMessageAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_SET_MUTLI_SIGN_UP_EDIT_MESSAGE,
    payload: {
        editMessage
    },
});

export const UpdateTemplateSaveSettings = (settings: TemplateSaveSettings): UpdateTemplateSaveSettingsAction => ({
    type: LandingPageCreatorActionTypes.UPDATE_TEMPLATE_SAVE_SETTINGS,
    payload: settings,
});

// export const TemplateSaveStart = ({
//     data,
//     exitOnSave,
//     errors
// }: {
//     data: EditorSaveData;
//     exitOnSave: boolean;
//     errors?: EditorSaveError[]
// }): TemplateSaveStartAction => ({
//     type: LandingPageCreatorActionTypes.TEMPLATE_SAVE_START,
//     payload: { data, exitOnSave, errors },
//     meta: {
//         thunk: true
//     }
// });

// export const TemplateSaveSuccess = (data: EditorSaveData): TemplateSaveSuccessAction => ({
//     type: LandingPageCreatorActionTypes.TEMPLATE_SAVE_SUCCESS,
//     payload: {
//         data,
//     }
// });

// export const TemplateSaveError = (nameError?): TemplateSaveErrorAction => ({
//     type: LandingPageCreatorActionTypes.TEMPLATE_SAVE_ERROR,
//     payload: { nameError },
// });

export const SetIsTemplateOpen = (isOpen: boolean): SetIsTemplateOpenAction => ({
    type: LandingPageCreatorActionTypes.SET_IS_TEMPLATE_OPEN,
    payload: { isOpen },
});

export const SetTemplatePreview = (content?: string, name?: string, isPublic?: boolean): SetTemplatePreviewAction => ({
    type: LandingPageCreatorActionTypes.SET_TEMPLATE_PREVIEW,
    payload: { content, name, isPublic },
});

export const Reset = (): ResetAction => ({
    type: LandingPageCreatorActionTypes.RESET,
});

export const TemplateShowSaveErrors = (): TemplateShowSaveErrorsAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_SHOW_SAVE_ERRORS,
});

export const TemplateChanged = (): TemplateChangedAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_CHANGED,
});

export const TemplateDeselect = (): TemplateDeselectAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_DESELECT,
});

export const OpenUpsellingOverlay = (feature: AvailableFeaturesUpselling): OpenUpsellingOverlayAction => ({
    type: LandingPageCreatorActionTypes.OPEN_UPSELLING_OVERLAY,
    payload: {
        feature,
    },
});

export const CloseUpsellingOverlay = (): CloseUpsellingOverlayAction => ({
    type: LandingPageCreatorActionTypes.CLOSE_UPSELLING_OVERLAY,
});

export const OpenGallery = (): OpenGalleryAction => ({
    type: LandingPageCreatorActionTypes.OPEN_GALLERY,
});

export const CloseGallery = (): CloseGalleryAction => ({
    type: LandingPageCreatorActionTypes.CLOSE_GALLERY,
});

export const TemplatePreviewFromGallery = (
    templateName?: string,
    isPublic?: boolean,
    templateType?: TemplateType
): TemplatePreviewFromGalleryAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_PREVIEW_FROM_GALLERY,
    payload: {
        templateName,
        isPublic,
        templateType
    },
});

export const TemplateOpenFromGallery = (templateName?: string, isPublic?: boolean): TemplateOpenFromGalleryAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_OPEN_FROM_GALLERY,
    payload: {
        templateName,
        isPublic,
    },
});

export const TemplateLoadSingleStart = (templateName?: string, isPublic?: boolean): TemplateLoadSingleStartAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_LOAD_SINGLE_START,
    payload: {
        templateName,
        isPublic,
    },
    meta: {
        thunk: true,
    },
});

export const TemplateLoadSingleSuccess = (
    template: Template,
    meta?: Record<string, unknown>
): TemplateLoadSingleSuccessAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_LOAD_SINGLE_SUCCESS,
    payload: { template },
    meta,
});

export const TemplateLoadSingleError = (meta?: Record<string, unknown>): TemplateLoadSingleErrorAction => ({
    type: LandingPageCreatorActionTypes.TEMPLATE_LOAD_SINGLE_ERROR,
    meta,
    error: true,
});

export const SetLandingPageErrors = (errors?: LandingPageErrors): SetLandingPageErrorsAction => ({
    type: LandingPageCreatorActionTypes.SET_LANDING_PAGE_ERRORS,
    payload: {
        errors,
    },
});

export const AddContactListStart = (blockClientId: string, list: ListPayload): AddContactListStartAction => ({
    type: LandingPageCreatorActionTypes.ADD_CONTACT_LIST_START,
    payload: {
        blockClientId,
        list,
    },
});

export const AddContactListErrorStatus = (isAddListError: boolean): AddContactListErrorStatusAction => ({
    type: LandingPageCreatorActionTypes.ADD_CONTACT_LIST_ERROR_STATUS,
    payload: {
        isAddListError,
    },
});

export const getLandingPageCreatorStore = (store: ApplicationState): LandingPageCreatorState =>
    store.landingPageCreator;

export const getLandingPageCreatorTemplateFilesStore = (store: ApplicationState): FilesType =>
    store.landingPageCreator.template.files;

export const getLandingPageCreatorGalleryOpenStore = (state: ApplicationState): boolean =>
    state?.landingPageCreator?.isGalleryOpen;
